import { Icon } from '@iconify/react/dist/iconify.js';
import { useEffect, useId, useRef, useState } from 'react';
import useErrors from '../hook/useErrors';
import server, { API_ORIGIN } from '../services/server';

const MAX_PEDIDO_IMAGES = 10;

function urlPublicaArquivo(a, pedidoId) {
    if (!a) return '';
    if (a.url) return a.url;

    let p = String(a.path || '').replace(/\\/g, '/');
    if (p.startsWith('local:')) {
        p = p.slice('local:'.length);
    }
    if (pedidoId && p.startsWith('pedido-imagens/')) {
        p = `pedido-${pedidoId}/${p.slice('pedido-imagens/'.length)}`;
    }
    if (p) return `${API_ORIGIN}/uploads/${p}`;
    return '';
}

function SavedImageCard({ image, pedidoId, deletingId, onDelete }) {
    const src = urlPublicaArquivo(image, pedidoId);

    return (
        <div className="position-relative border rounded overflow-hidden bg-light" style={{ width: '220px', height: '220px' }}>
            <a href={src || '#'} target="_blank" rel="noopener noreferrer" className="d-block w-100 h-100">
                <img
                    src={src}
                    alt={image.nome || ''}
                    className="w-100 h-100"
                    style={{ objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                />
            </a>
            <button
                type="button"
                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1 rounded-circle"
                title="Excluir imagem"
                disabled={deletingId === image.id}
                onClick={() => onDelete(image)}
            >
                <Icon icon="mdi:trash-can-outline" width="18" height="18" />
            </button>
            <div className="position-absolute bottom-0 start-0 end-0 px-10 py-8" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 100%)' }}>
                <small className="d-block text-truncate text-dark" title={image.nome}>
                    {image.nome}
                </small>
            </div>
        </div>
    );
}

function QueuePreviewCard({ item, submitting, onRemove }) {
    return (
        <div className="position-relative border rounded overflow-hidden bg-light" style={{ width: '56px', height: '56px' }}>
            <img
                src={item.url}
                alt={item.file.name}
                className="w-100 h-100"
                style={{ objectFit: 'cover' }}
            />
            <button
                type="button"
                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0 rounded-circle"
                title="Remover da fila"
                disabled={submitting}
                onClick={() => onRemove(item.id)}
                style={{ width: '22px', height: '22px', lineHeight: 1 }}
            >
                <Icon icon="mdi:close" width="14" height="14" />
            </button>
        </div>
    );
}

function PedidoImagesTab({ data, mutate }) {
    const { setErrors, toast } = useErrors();
    const inputId = useId();
    const [items, setItems] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    useEffect(() => {
        return () => {
            itemsRef.current.forEach((item) => URL.revokeObjectURL(item.url));
        };
    }, []);

    const clearLocalPreviews = () => {
        setItems((prev) => {
            prev.forEach((item) => URL.revokeObjectURL(item.url));
            return [];
        });
    };

    const onSelectFiles = (e) => {
        const list = Array.from(e.target.files || []).filter((file) => file.type.startsWith('image/'));

        setItems((prev) => {
            const availableSlots = Math.max(0, MAX_PEDIDO_IMAGES - prev.length);
            if (availableSlots === 0) {
                toast.error(`O limite maximo por envio e de ${MAX_PEDIDO_IMAGES} imagens.`);
                return prev;
            }

            const acceptedList = list.slice(0, availableSlots);
            if (acceptedList.length < list.length) {
                toast.error(`A fila aceita no maximo ${MAX_PEDIDO_IMAGES} imagens por envio.`);
            }

            const added = acceptedList.map((file) => ({
                id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                file,
                url: URL.createObjectURL(file),
            }));
            return [...prev, ...added];
        });
        e.target.value = '';
    };

    const removeItem = (id) => {
        setItems((prev) => {
            const found = prev.find((item) => item.id === id);
            if (found) URL.revokeObjectURL(found.url);
            return prev.filter((item) => item.id !== id);
        });
    };

    const handleEnviar = async () => {
        if (!data?.id || items.length === 0) return;

        setSubmitting(true);
        try {
            const fd = new FormData();
            items.forEach((item) => fd.append('imagens', item.file));
            await server.post(`pedido/${data.id}/arquivos`, fd);
            toast.success('Imagens enviadas com sucesso.');
            clearLocalPreviews();
            if (typeof mutate === 'function') await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteSaved = async (image) => {
        if (!data?.id || !image?.id) return;

        setDeletingId(image.id);
        try {
            await server.delete(`pedido/${data.id}/arquivos/${image.id}`);
            toast.success('Imagem removida com sucesso.');
            if (typeof mutate === 'function') await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setDeletingId(null);
        }
    };

    const savedImages = Array.isArray(data?.arquivo)
        ? data.arquivo
        : Array.isArray(data?.arquivos)
            ? data.arquivos
            : [];

    if (!data?.id) {
        return (
            <div className="border border-dashed rounded py-5 px-4 text-center text-secondary-light">
                <Icon icon="mdi:image-off-outline" width="40" height="40" className="mb-2 opacity-50" />
                <p className="mb-0">Salve o pedido primeiro para gerenciar as imagens.</p>
            </div>
        );
    }

    return (
        <div className="tab-pane fade show active">
            <div className="border radius-8 bg-white px-12 py-12 mb-20">
                <div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between gap-3">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <input
                            id={inputId}
                            type="file"
                            className="d-none"
                            accept="image/*"
                            multiple
                            disabled={submitting}
                            onChange={onSelectFiles}
                        />
                        <label htmlFor={inputId} className="btn btn-primary border border-primary-600 text-md px-20 py-10 radius-8 mb-0">
                            Adicionar imagens
                        </label>
                        <button
                            type="button"
                            className="btn btn-light border text-md px-16 py-10 radius-8"
                            disabled={submitting || items.length === 0}
                            onClick={clearLocalPreviews}
                        >
                            Limpar
                        </button>
                        <button
                            type="button"
                            className="btn btn-success border border-success text-md px-20 py-10 radius-8"
                            disabled={submitting || items.length === 0}
                            onClick={handleEnviar}
                        >
                            {submitting ? 'Salvando...' : 'Salvar'}
                        </button>
                        <small className="text-secondary-light ms-xl-2">Max. 10 imagens</small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {items.length > 0 ? (
                            items.map((item) => (
                                <QueuePreviewCard
                                    key={item.id}
                                    item={item}
                                    submitting={submitting}
                                    onRemove={removeItem}
                                />
                            ))
                        ) : (
                            <small className="text-secondary-light">Nenhuma imagem selecionada.</small>
                        )}
                    </div>
                </div>
            </div>

            <div className="mb-12">
                <h6 className="mb-0">Imagens do pedido</h6>
            </div>

            <div className="border radius-8 bg-white p-20">
                {savedImages.length > 0 ? (
                    <div className="d-flex gap-3 flex-wrap">
                        {savedImages.map((image) => (
                            <SavedImageCard
                                key={image.id}
                                image={image}
                                pedidoId={data.id}
                                deletingId={deletingId}
                                onDelete={handleDeleteSaved}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center text-secondary-light border border-dashed rounded py-5 px-3">
                        <Icon icon="mdi:image-off-outline" width="40" height="40" className="mb-2 opacity-50" />
                        <p className="mb-0 small">Nenhuma imagem salva neste pedido.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PedidoImagesTab;
